import React, { useState } from 'react';
import './Navbar.css';
import { useAuth } from '@/hooks/useAuth';
import { LogOut, User, QrCode, Menu, X, Wallet, MessageSquare } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useFeedbackStore } from '@/stores';
import { FeedbackModal } from '@/components';

const Navbar: React.FC = () => {
  const { account, logout, getAccessToken } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Feedback
  const openFeedback = useFeedbackStore(state => state.openFeedback);
  const closeFeedback = useFeedbackStore(state => state.closeFeedback);
  const submitFeedback = useFeedbackStore(state => state.submitFeedback);
  const feedbackOpen = useFeedbackStore(state => state.isOpen);
  const feedbackSource = useFeedbackStore(state => state.activeSource);
  const feedbackSubmitting = useFeedbackStore(state => state.loading);
  const feedbackError = useFeedbackStore(state => state.error);

  const handleSubmitFeedback = async (submission: any) => {
    try {
      const token = await getAccessToken(true);
      await submitFeedback(submission, token);
    } catch {
      // error already set in store
    }
  };

  const handleLogout = () => {
    logout();
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <Link to="/" className="navbar-brand" onClick={closeMobileMenu}>
            <QrCode size={24} />
            <span className="brand-text">Digital Business Card</span>
          </Link>

          {/* Mobile Menu Toggle */}
          <button 
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'active' : ''}`}
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Desktop & Mobile Menu */}
          <div className={`navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
            <div className="navbar-links">
              <Link 
                to="/" 
                className={`navbar-link ${location.pathname === '/' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <User size={18} />
                <span>Dashboard</span>
              </Link>
              <Link 
                to="/cards" 
                className={`navbar-link ${location.pathname.startsWith('/cards') ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <QrCode size={18} />
                <span>My Cards</span>
              </Link>
              <Link 
                to="/wallet" 
                className={`navbar-link ${location.pathname === '/wallet' ? 'active' : ''}`}
                onClick={closeMobileMenu}
              >
                <Wallet size={18} />
                <span>Wallet</span>
              </Link>

              {/* Mobile only: Feedback as a nav-style item */}
              <button
                className="navbar-link navbar-feedback-mobile"
                onClick={() => { openFeedback('navbar'); closeMobileMenu(); }}
              >
                <MessageSquare size={18} />
                <span>Give Feedback</span>
              </button>
            </div>

            <div className="navbar-user">
              <div className="user-info">
                <User size={18} />
                <span className="user-name">{account?.name || 'User'}</span>
              </div>

              {/* Desktop only: icon feedback button */}
              <button
                className="btn-feedback"
                onClick={() => openFeedback('navbar')}
                title="Give Feedback"
                aria-label="Give Feedback"
              >
                <MessageSquare size={18} />
              </button>

              <button onClick={handleLogout} className="btn-logout">
                <LogOut size={18} />
                <span>Logout</span>
              </button>
            </div>
          </div>

          {/* Mobile Menu Overlay */}
          {mobileMenuOpen && (
            <div className="mobile-overlay" onClick={closeMobileMenu}></div>
          )}
        </div>
      </nav>

      {/* Global Feedback Modal */}
      {feedbackOpen && feedbackSource && (
        <FeedbackModal
          isOpen={feedbackOpen}
          source={feedbackSource}
          isSubmitting={feedbackSubmitting}
          error={feedbackError}
          onSubmit={handleSubmitFeedback}
          onClose={closeFeedback}
        />
      )}
    </>
  );
};

export default Navbar;
